import React, { useMemo, useState } from 'react';

// 🗃️ KeepsakeShelf — V1 idea 7 (IMAGINE_REACTIVE_WINDOWS_SPEC §9.5, ruled
// 08-28): everything you two made, in one place. Pure derivation from the
// action rows the page already loaded — /imagine //build /tinker art and
// doodles all land as shared_content rows with data.url; no query, no LLM.
// "Postcard" export v1 = open full size + copy link (honest scope: the media
// URL is the artifact; a composited postcard frame is a later nicety).
//
// 🏮 Lamplight dress (Nina 08-28): the grid became a SHELF — polaroid mattes
// with tape and tilt, captions in the caption's own voice, a wooden rail
// underneath. Derivation and behavior unchanged.

const KEEP_TYPES = new Set(['image', 'image_url', 'drawing']);
const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';
const TILTS = [-2, 1.6, -0.6];

const KeepsakeShelf = ({ actions = [] }) => {
  const [copiedId, setCopiedId] = useState(null);
  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (let i = actions.length - 1; i >= 0 && out.length < 24; i -= 1) {
      const a = actions[i];
      const d = a?.data;
      if (!a || a.type !== 'shared_content' || !d?.url || !KEEP_TYPES.has(d.content_type)) continue;
      if (d.is_reaction_gif) continue; // reaction GIFs are punctuation, not keepsakes
      if (seen.has(d.url)) continue;
      seen.add(d.url);
      out.push({
        id: a.id,
        url: d.url,
        caption: d.description || '',
        by: d.sender_name || '',
        isDoodle: d.content_type === 'drawing',
      });
    }
    return out;
  }, [actions]);

  const copyLink = (item) => {
    try {
      navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) { window.open(item.url, '_blank', 'noopener'); }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em',
        color: '#E8BC72', marginBottom: 10,
      }}>
        🗃️ KEEPSAKES · {items.length ? `${items.length} thing${items.length === 1 ? '' : 's'} made here` : 'things made here'}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: '0.78em', color: 'rgba(255,238,216,0.5)', fontStyle: 'italic' }}>
          Nothing on the shelf yet. Ask for something, or draw them something.
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
            gap: 12, padding: '4px 4px 0',
          }}>
            {items.map((item, i) => (
              <div key={item.id} style={{ position: 'relative' }}>
                <div style={{
                  position: 'relative', background: '#F3EDE2', borderRadius: 2, textShadow: 'none',
                  padding: `6px 6px ${item.caption ? 22 : 12}px`,
                  transform: `rotate(${TILTS[i % TILTS.length]}deg)`,
                  boxShadow: '0 6px 14px rgba(6,3,10,0.45)',
                }}>
                  <span style={{
                    position: 'absolute', top: -7, left: '50%', width: 40, height: 13, marginLeft: -20,
                    background: 'rgba(255,244,214,0.6)', transform: 'rotate(-3deg)',
                    borderLeft: '1px dashed rgba(120,90,40,0.2)', borderRight: '1px dashed rgba(120,90,40,0.2)',
                  }} />
                  <button
                    onClick={() => window.open(item.url, '_blank', 'noopener')}
                    title={item.caption || (item.isDoodle ? 'a doodle' : 'open full size')}
                    aria-label={`Open keepsake${item.caption ? `: ${item.caption}` : ''}`}
                    style={{
                      display: 'block', width: '100%', aspectRatio: '1 / 1', padding: 0,
                      border: '1px solid rgba(90,72,50,0.25)', borderRadius: 1,
                      overflow: 'hidden', cursor: 'pointer', background: 'rgba(0,0,0,0.3)',
                    }}
                  >
                    <img
                      src={item.url}
                      alt={item.caption || 'keepsake'}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                  {item.caption && (
                    <span style={{
                      position: 'absolute', left: 4, right: 4, bottom: 4, textAlign: 'center',
                      fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5, color: '#5A4832',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {item.caption}
                    </span>
                  )}
                  <button
                    onClick={() => copyLink(item)}
                    aria-label="Copy keepsake link"
                    style={{
                      position: 'absolute', right: 3, top: 3, minWidth: 34, minHeight: 26,
                      fontSize: '0.66em', border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: 6, cursor: 'pointer', color: '#fff',
                      background: 'rgba(10,8,16,0.88)',
                    }}
                  >{copiedId === item.id ? '✓' : '🔗'}</button>
                  {item.isDoodle && (
                    <span style={{
                      position: 'absolute', left: 3, top: 3, fontSize: '0.7em',
                      background: 'rgba(10,8,16,0.8)', borderRadius: 5, padding: '1px 4px',
                    }}>🖍️</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* the rail everything sits on */}
          <div style={{
            height: 11, margin: '10px 2px 0', borderRadius: 3,
            background: 'linear-gradient(180deg, #5A4030, #3C2A20)',
            borderTop: '1px solid rgba(232,188,114,0.45)',
            boxShadow: '0 8px 14px rgba(6,3,10,0.4)',
          }} />
        </>
      )}
    </div>
  );
};

export default KeepsakeShelf;
